/**
 * Creation v2 (docs/CREATION-V2.md, docs/API.md "Creation v2"): identity → launch (one wallet transaction: the ETH
 * creation fee) → the agent in `setting_up` with its token launching → basics (model slot `creation.basics`) → the
 * interview in the agent's thread (chat role `interview`) → a dry-run `deployment` proposal whose approval makes
 * version 1.
 */
import { z } from 'zod'
import { Venue } from '../enums.js'
import { Address, Handle, IdempotencyKey, IsoTimestamp, UsdString, Uuid, WeiString } from '../primitives.js'
import { AgentAvatar } from './avatar.js'
import { AgentSummary } from './common.js'
import { AgentTemplateId } from './algorithms.js'
import { PaymentIntent } from './billing.js'
import { UnsignedTx } from './tokens.js'

// ------------------------------------------------------------------------------------------------ ticker

/** A creation-v2 ticker: 3 to 6 upper-case letters. */
export const TICKER_PATTERN = /^[A-Z]{3,6}$/
export const Ticker = z.string().regex(TICKER_PATTERN, 'ticker must be 3-6 upper-case letters')
export type Ticker = z.output<typeof Ticker>

/**
 * A ticker suggested from an agent name: the initials of a multi-word name (padded with the first word's next
 * letters), else the first letters of a single word, upper-cased, 3 to 6 letters; `AGENT` when the name has fewer
 * than three letters.
 */
export function suggestTicker(name: string): string {
  const words = name
    .normalize('NFKD')
    .replace(/[^A-Za-z\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
  const letters = words.join('')
  if (letters.length < 3) return 'AGENT'
  if (words.length >= 3) return words.map((w) => w[0]).join('').slice(0, 6).toUpperCase()
  if (words.length === 2) {
    const [a, b] = words as [string, string]
    return (a.slice(0, Math.max(2, 4 - Math.min(b.length, 2))) + b.slice(0, 2)).slice(0, 6).toUpperCase()
  }
  return letters.slice(0, Math.min(6, Math.max(3, letters.length >= 5 ? 5 : letters.length))).toUpperCase()
}

export const TickerAvailabilityQuery = z.object({
  ticker: z.string().max(16),
  /** The agent name, for the suggestion when the ticker is taken or invalid. */
  name: z.string().max(80).optional(),
})
export type TickerAvailabilityQuery = z.output<typeof TickerAvailabilityQuery>

export const TickerUnavailableReason = z.enum(['format', 'taken', 'reserved'])
export const TickerAvailability = z.object({
  ticker: z.string(),
  valid: z.boolean(),
  available: z.boolean(),
  /** `format`: not 3-6 upper-case letters; `taken`: an agent token has it; `reserved`: an open launch holds it. */
  reason: TickerUnavailableReason.nullable(),
  /** A free ticker close to it (or from the name), null when none was found. */
  suggestion: Ticker.nullable(),
})
export type TickerAvailability = z.output<typeof TickerAvailability>

// ------------------------------------------------------------------------------------------------ basics

export const BASICS_STYLES = ['trend', 'breakout', 'momentum', 'copy', 'dip_buy', 'custom'] as const
export const BasicsStyle = z.enum(BASICS_STYLES)
export type BasicsStyle = z.output<typeof BasicsStyle>

export const RiskBand = z.enum(['careful', 'steady', 'bold'])
export type RiskBand = z.output<typeof RiskBand>

/** Top-level basics fields the interview may ask about and a patch may change. */
export const BasicsField = z.enum(['style', 'universe', 'risk', 'cadence', 'exits', 'template'])
export type BasicsField = z.output<typeof BasicsField>

const Pct = (max: number) => z.number().min(0.5).max(max)

export const BasicsUniverse = z.object({
  /** Which tokens: fresh launches, established tokens, any, or only the listed ones. */
  scope: z.enum(['new_launches', 'established', 'any', 'list']),
  /** Symbols or addresses named by the creator (only these for `list`). */
  tokens: z.array(z.string().min(1).max(64)).max(20),
  minLiquidityUsd: UsdString.nullable(),
  minAgeHours: z.number().int().min(0).max(8760).nullable(),
  maxAgeHours: z.number().int().min(1).max(8760).nullable(),
  /** Further token rules in plain words ("skip tokens with a mint function"). */
  rules: z.array(z.string().min(1).max(200)).max(8),
})
export type BasicsUniverse = z.output<typeof BasicsUniverse>

export const BasicsRisk = z.object({
  band: RiskBand,
  /** Cap per trade in USD (decimal string). */
  perTradeUsd: UsdString,
  maxOpenPositions: z.number().int().min(1).max(20),
  /** Stop entering for the day after this loss (USD), null = none. */
  dailyLossLimitUsd: UsdString.nullable(),
})
export type BasicsRisk = z.output<typeof BasicsRisk>

export const BasicsExits = z.object({
  stopLossPct: Pct(90).nullable(),
  takeProfitPct: Pct(1000).nullable(),
  trailingStopPct: Pct(90).nullable(),
  maxHoldHours: z.number().int().min(1).max(720).nullable(),
})
export type BasicsExits = z.output<typeof BasicsExits>

export const BasicsOpenItem = z.object({ key: BasicsField, question: z.string().min(1).max(240) })
export type BasicsOpenItem = z.output<typeof BasicsOpenItem>

/** What the model produces from the description (the `emit_basics` tool's argument, validated). */
export const AgentBasicsDraft = z.object({
  style: BasicsStyle,
  styleNote: z.string().min(1).max(200),
  universe: BasicsUniverse,
  risk: BasicsRisk,
  cadence: z.object({ tickIntervalS: z.number().int().min(60).max(86_400) }),
  exits: BasicsExits,
  /** The closest agent template (`AGENT_TEMPLATE_BUNDLES`) and why; null when none fits. */
  template: z.object({ id: AgentTemplateId, why: z.string().min(1).max(240) }).nullable(),
  /** What the description left open: the interview asks about these (at most six). */
  open: z.array(BasicsOpenItem).max(6),
  summary: z.string().min(1).max(800),
})
export type AgentBasicsDraft = z.output<typeof AgentBasicsDraft>

/** The stored basics of a launched agent (`agents.creation_launches.basics`). */
export const AgentBasics = AgentBasicsDraft.extend({
  schema: z.literal('agent-basics/1'),
  /** Bumped by every change (model or creator). */
  version: z.number().int().min(1),
  /** The creator asked to start from the closest template ("Start from a template"). */
  startFromTemplate: z.boolean(),
  updatedAt: IsoTimestamp,
})
export type AgentBasics = z.output<typeof AgentBasics>

/** A change to basics: any top-level field (nested objects are merged one level deep). */
export const BasicsPatch = z
  .object({
    style: BasicsStyle,
    styleNote: z.string().min(1).max(200),
    universe: BasicsUniverse.partial(),
    risk: BasicsRisk.partial(),
    cadence: z.object({ tickIntervalS: z.number().int().min(60).max(86_400) }),
    exits: BasicsExits.partial(),
    template: z.object({ id: AgentTemplateId, why: z.string().min(1).max(240) }).nullable(),
    startFromTemplate: z.boolean(),
    summary: z.string().min(1).max(800),
  })
  .partial()
  .strict()
export type BasicsPatch = z.output<typeof BasicsPatch>

export const PatchBasics = z.object({
  patch: BasicsPatch,
  /** Refuse with 409 `CONFLICT` when the stored basics moved past this version (another edit or the interview). */
  expectedVersion: z.number().int().min(1).optional(),
})
export type PatchBasics = z.output<typeof PatchBasics>

/** Body of the `basics` status message in the agent's thread (C5 renders it as the basics card). */
export const BasicsMessageBody = z.object({
  schema: z.literal('agent-basics/1'),
  type: z.literal('basics'),
  agentId: Uuid,
  basics: AgentBasics,
  version: z.number().int().min(1),
  /** The change in plain words (empty for the first basics). */
  changes: z.array(z.string()),
  editedBy: z.enum(['model', 'creator', 'interview']),
  text: z.string(),
})
export type BasicsMessageBody = z.output<typeof BasicsMessageBody>

/** At most this many interview questions (the first comes with the basics). */
export const INTERVIEW_MAX_QUESTIONS = 6

export const CreationInterviewStatus = z.enum(['waiting_basics', 'asking', 'finishing', 'proposed', 'done'])
export type CreationInterviewStatus = z.output<typeof CreationInterviewStatus>

/** Interview progress (`agents.creation_launches.interview`). */
export const CreationInterviewState = z.object({
  status: CreationInterviewStatus,
  /** Questions asked so far (≤ `INTERVIEW_MAX_QUESTIONS`). */
  asked: z.number().int().min(0).max(INTERVIEW_MAX_QUESTIONS),
  /** Keys asked, in order. */
  keys: z.array(z.string()).max(INTERVIEW_MAX_QUESTIONS),
  /** The user message whose authoring run proposes version 1 (the run id). */
  authoringRunId: Uuid.nullable(),
})
export type CreationInterviewState = z.output<typeof CreationInterviewState>

// ------------------------------------------------------------------------------------------------ launch

export const LaunchAgent = z.object({
  /** Client-generated key; the same key returns the same launch (idempotent per user). */
  clientKey: IdempotencyKey,
  name: z.string().trim().min(1).max(40),
  ticker: Ticker,
  /** One paragraph: what should this agent do? */
  description: z.string().trim().min(20).max(1000),
  /** The 3D mascot of the Look step (lane AVATAR-3D); null → `defaultAvatarFor(handle)`. */
  avatar: AgentAvatar.nullable().optional(),
  /** Default `robinhood`; a venue that is not live is 409 `VENUE_NOT_AVAILABLE`. */
  venue: Venue.optional(),
})
export type LaunchAgent = z.output<typeof LaunchAgent>

export const LaunchParams = z.object({ id: Uuid })

export const CreationLaunchStatus = z.enum(['awaiting_payment', 'launched', 'expired'])
export type CreationLaunchStatus = z.output<typeof CreationLaunchStatus>

/**
 * `unavailable` (lane ALGOBOO1, FEE1): the agent was created free while the platform could not launch tokens; its
 * token launches later, for the creation fee, when the platform can (the manage page's Token section). `failed`: the
 * platform refused or reverted the launch; nothing further is charged and a charged fee is refunded.
 */
export const LaunchTokenStatus = z.enum(['none', 'unavailable', 'launching', 'live', 'failed'])
export type LaunchTokenStatus = z.output<typeof LaunchTokenStatus>

/** The platform's refund of a fee charged for a launch it refused (`POST /v2/accounts/refunds`, decided by its operator). */
export const LaunchRefundState = z.enum(['requested', 'approved', 'declined', 'paying', 'paid', 'failed'])
export type LaunchRefundState = z.output<typeof LaunchRefundState>

export const LaunchRefund = z.object({
  state: LaunchRefundState,
  refundRef: z.string(),
  amountWei: WeiString,
  /** The transaction that paid it back, once `paid`. */
  paidTxHash: z.string().nullable(),
  updatedAt: IsoTimestamp.nullable(),
})
export type LaunchRefund = z.output<typeof LaunchRefund>

/**
 * GET /agents/launch-terms: what `/create` charges now and what it includes, from the platform's own answer. While
 * the platform cannot launch a token (`tokenLaunch.available` false: its contracts are not deployed on the chain, its
 * operator account is not ready, or it is unarmed -- `code` the platform's, `reason` in words), creating an agent is
 * free and includes no token; with a launch, the creation fee pays for both.
 */
export const LaunchTerms = z.object({
  tokenLaunch: z.object({ available: z.boolean(), code: z.string().nullable(), reason: z.string().nullable() }),
  /** Wei charged by `POST /agents/launch` now: the creation fee with a launch, `0` without one. */
  feeWei: WeiString,
  /** The creation fee the launch costs when it is available. */
  launchFeeWei: WeiString,
  checkedAt: IsoTimestamp,
})
export type LaunchTerms = z.output<typeof LaunchTerms>

/** One refund of the caller's fees (GET /me/refunds). */
export const MyRefund = LaunchRefund.extend({ launchId: Uuid, ticker: Ticker, agentHandle: Handle.nullable(), requestedAt: IsoTimestamp.nullable() })
export type MyRefund = z.output<typeof MyRefund>
export const MyRefunds = z.object({ items: z.array(MyRefund) })
export type MyRefunds = z.output<typeof MyRefunds>

export const CreationLaunch = z.object({
  id: Uuid,
  clientKey: z.string(),
  status: CreationLaunchStatus,
  name: z.string(),
  ticker: Ticker,
  handle: Handle,
  description: z.string(),
  avatar: AgentAvatar.nullable(),
  venue: Venue,
  /** The fee in wei (native ETH) and the intent that pays it; null when an unused creation credit paid. */
  feeWei: WeiString,
  payment: PaymentIntent.nullable(),
  /** Wallet transactions still to send: `[payCreation]` while awaiting payment, else empty. */
  txs: z.array(UnsignedTx),
  /** The agent once the payment is confirmed (state `setting_up` until version 1 is approved). */
  agent: AgentSummary.extend({ status: z.string() }).nullable(),
  token: z.object({
    status: LaunchTokenStatus,
    symbol: Ticker,
    /** Predicted (CREATE2) from the launch transaction on, confirmed by the indexer. */
    address: Address.nullable(),
    launchTxHash: z.string().nullable(),
    /** The launch refusal (`LaunchRefusalCode`) when `failed`. */
    refusal: z.string().nullable(),
  }),
  /** The agent's thread where basics and the interview happen (`/desk?thread=<id>`). */
  threadId: Uuid.nullable(),
  basicsStatus: z.enum(['pending', 'ready', 'failed']),
  basics: AgentBasics.nullable(),
  interview: CreationInterviewState.nullable(),
  /** Typed refusal of the launch so far (`FEE_UNPAID`, `LAUNCH_FAILED`), null when none. */
  refusal: z.object({ code: z.enum(['FEE_UNPAID', 'LAUNCH_FAILED']), message: z.string() }).nullable(),
  /** `included`: the fee paid for the agent and its token launch; `deferred`: created free, the token launches later. */
  tokenLaunch: z.enum(['included', 'deferred']),
  /** The refund of a fee charged for a launch the platform refused, null when none is due or filed. */
  refund: LaunchRefund.nullable(),
  createdAt: IsoTimestamp,
  updatedAt: IsoTimestamp,
})
export type CreationLaunch = z.output<typeof CreationLaunch>

// ------------------------------------------------------------------------------------------------ basics helpers

/** Apply a patch to basics: top-level fields replace, the nested objects merge one level deep; `version` + 1. */
export function mergeBasics(prev: AgentBasics, patch: BasicsPatch, now: Date): AgentBasics {
  const next = {
    ...prev,
    ...(patch.style !== undefined ? { style: patch.style } : {}),
    ...(patch.styleNote !== undefined ? { styleNote: patch.styleNote } : {}),
    ...(patch.universe ? { universe: { ...prev.universe, ...patch.universe } } : {}),
    ...(patch.risk ? { risk: { ...prev.risk, ...patch.risk } } : {}),
    ...(patch.cadence ? { cadence: { ...prev.cadence, ...patch.cadence } } : {}),
    ...(patch.exits ? { exits: { ...prev.exits, ...patch.exits } } : {}),
    ...(patch.template !== undefined ? { template: patch.template } : {}),
    ...(patch.startFromTemplate !== undefined ? { startFromTemplate: patch.startFromTemplate } : {}),
    ...(patch.summary !== undefined ? { summary: patch.summary } : {}),
    version: prev.version + 1,
    updatedAt: now.toISOString(),
  }
  return AgentBasics.parse(next)
}

const STYLE_WORDS: Record<BasicsStyle, string> = { trend: 'trend following', breakout: 'breakouts', momentum: 'momentum', copy: 'copying wallets', dip_buy: 'buying dips', custom: 'a custom style' }
const SCOPE_WORDS: Record<BasicsUniverse['scope'], string> = { new_launches: 'new launches', established: 'established tokens', any: 'any token', list: 'only the listed tokens' }

/** Cadence in words: "every 5 minutes", "every hour", "every day". */
export function cadenceWords(s: number): string {
  if (s % 86_400 === 0) return s === 86_400 ? 'every day' : `every ${s / 86_400} days`
  if (s % 3600 === 0) return s === 3600 ? 'every hour' : `every ${s / 3600} hours`
  if (s % 60 === 0) return s === 60 ? 'every minute' : `every ${s / 60} minutes`
  return `every ${s} seconds`
}

const pct = (v: number | null) => (v === null ? 'none' : `${v} %`)
const usdWords = (v: string | null) => (v === null ? 'none' : `$${v}`)

/** What changed between two basics, in plain words (empty when nothing did). */
export function basicsChanges(prev: AgentBasics, next: AgentBasics): string[] {
  const out: string[] = []
  const ch = (label: string, a: string, b: string) => {
    if (a !== b) out.push(`${label}: ${a} → ${b}`)
  }
  ch('Style', STYLE_WORDS[prev.style], STYLE_WORDS[next.style])
  ch('Tokens', SCOPE_WORDS[prev.universe.scope], SCOPE_WORDS[next.universe.scope])
  ch('Token list', prev.universe.tokens.join(', ') || 'none', next.universe.tokens.join(', ') || 'none')
  ch('Minimum liquidity', usdWords(prev.universe.minLiquidityUsd), usdWords(next.universe.minLiquidityUsd))
  ch('Minimum token age', prev.universe.minAgeHours === null ? 'none' : `${prev.universe.minAgeHours} h`, next.universe.minAgeHours === null ? 'none' : `${next.universe.minAgeHours} h`)
  ch('Maximum token age', prev.universe.maxAgeHours === null ? 'none' : `${prev.universe.maxAgeHours} h`, next.universe.maxAgeHours === null ? 'none' : `${next.universe.maxAgeHours} h`)
  ch('Token rules', prev.universe.rules.join('; ') || 'none', next.universe.rules.join('; ') || 'none')
  ch('Risk', prev.risk.band, next.risk.band)
  ch('Per trade', usdWords(prev.risk.perTradeUsd), usdWords(next.risk.perTradeUsd))
  ch('Open positions at most', String(prev.risk.maxOpenPositions), String(next.risk.maxOpenPositions))
  ch('Daily loss limit', usdWords(prev.risk.dailyLossLimitUsd), usdWords(next.risk.dailyLossLimitUsd))
  ch('Checks', cadenceWords(prev.cadence.tickIntervalS), cadenceWords(next.cadence.tickIntervalS))
  ch('Stop loss', pct(prev.exits.stopLossPct), pct(next.exits.stopLossPct))
  ch('Take profit', pct(prev.exits.takeProfitPct), pct(next.exits.takeProfitPct))
  ch('Trailing stop', pct(prev.exits.trailingStopPct), pct(next.exits.trailingStopPct))
  ch('Longest hold', prev.exits.maxHoldHours === null ? 'none' : `${prev.exits.maxHoldHours} h`, next.exits.maxHoldHours === null ? 'none' : `${next.exits.maxHoldHours} h`)
  ch('Closest template', prev.template?.id ?? 'none', next.template?.id ?? 'none')
  if (prev.startFromTemplate !== next.startFromTemplate) out.push(next.startFromTemplate ? 'Start from the template: yes' : 'Start from the template: no')
  if (prev.styleNote !== next.styleNote && out.length === 0) out.push(`Style note: ${next.styleNote}`)
  return out
}

/** One paragraph describing basics, for the authoring intent and the summary. */
export function basicsIntent(b: AgentBasics, description: string): string {
  const u = b.universe
  const lines = [
    `Build the strategy for this agent. The creator's description: ${description.trim()}`,
    `Style: ${STYLE_WORDS[b.style]} (${b.styleNote}).`,
    `Tokens: ${SCOPE_WORDS[u.scope]}${u.tokens.length ? ` (${u.tokens.join(', ')})` : ''}${u.minLiquidityUsd ? `, liquidity at least $${u.minLiquidityUsd}` : ''}${u.minAgeHours !== null ? `, at least ${u.minAgeHours} h old` : ''}${u.maxAgeHours !== null ? `, at most ${u.maxAgeHours} h old` : ''}.`,
    ...(u.rules.length ? [`Token rules: ${u.rules.join('; ')}.`] : []),
    `Risk: ${b.risk.band}; at most $${b.risk.perTradeUsd} per trade, ${b.risk.maxOpenPositions} open position${b.risk.maxOpenPositions === 1 ? '' : 's'}${b.risk.dailyLossLimitUsd ? `, stop entering after a $${b.risk.dailyLossLimitUsd} daily loss` : ''}.`,
    `Checks ${cadenceWords(b.cadence.tickIntervalS)}.`,
    `Exits: stop loss ${pct(b.exits.stopLossPct)}, take profit ${pct(b.exits.takeProfitPct)}, trailing stop ${pct(b.exits.trailingStopPct)}, longest hold ${b.exits.maxHoldHours === null ? 'none' : `${b.exits.maxHoldHours} h`}.`,
    `Summary: ${b.summary}`,
  ]
  return lines.join('\n')
}
